import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Container = styled.div`
  padding: 8rem 0;
  background: transparent;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 70% 20%, rgba(144, 19, 254, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 30% 80%, rgba(74, 144, 226, 0.08) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 2.5rem);
  text-align: center;
  margin-bottom: 3.5rem;
  color: #ffffff;
  font-weight: 800;
  font-family: "Outfit", "Inter", sans-serif;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  position: relative;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #4A90E2, #9013FE);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    margin-bottom: 2.5rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 4rem;
  gap: 1rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 3rem;
  }
`;

const FilterButton = styled.button`
  background: ${(props) =>
    props.active ? "rgba(74, 144, 226, 0.2)" : "rgba(255, 255, 255, 0.03)"};
  backdrop-filter: blur(10px);
  color: white;
  border: 1px solid ${(props) =>
    props.active ? "rgba(74, 144, 226, 0.5)" : "rgba(255, 255, 255, 0.1)"};
  padding: 0.75rem 1.75rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 30px;
  font-weight: 500;
  font-family: "Inter", sans-serif;
  font-size: 1rem;
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${(props) =>
    props.active ? "rgba(74, 144, 226, 0.25)" : "rgba(255, 255, 255, 0.08)"};
    border-color: ${(props) =>
    props.active ? "rgba(74, 144, 226, 0.6)" : "rgba(255, 255, 255, 0.2)"};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.9rem;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  padding: 0 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 1.5rem;
  }
`;

const ProjectCard = styled(motion.a)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  position: relative;
  
  /* Desktop Styles */
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transition: box-shadow 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.03);

  /* Reset Webkit filters specifically for mobile safari */
  @media (max-width: 768px) {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    background: rgba(30, 35, 45, 0.95);
  }
  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 19px;
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.15);
    pointer-events: none;
    z-index: 2;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    padding: 1px;
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.6), rgba(144, 19, 254, 0.6));
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4), 0 0 40px rgba(74, 144, 226, 0.1);
    
    &::after {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3) !important;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 240px;
  overflow: hidden;
  position: relative;
  background: ${(props) => props.$fallbackBg || "linear-gradient(135deg, #1c1c28, #2a2a3e)"};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.src ? 1 : 0)};

  ${ProjectCard}:hover & {
    transform: scale(1.05);
  }
`;

const FallbackText = styled.span`
  position: absolute;
  font-size: 1.5rem;
  font-family: "Outfit", sans-serif;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 0.1em;
`;

const ProjectInfo = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background: linear-gradient(180deg, rgba(25, 28, 36, 0) 0%, rgba(25, 28, 36, 0.8) 100%);
`;

const CategoryBadge = styled.span`
  background: rgba(74, 144, 226, 0.15);
  color: #4A90E2;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  align-self: flex-start;
  margin-bottom: 1rem;
`;

const ProjectTitle = styled.h3`
  margin-bottom: 1rem;
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 600;
  font-family: "Outfit", "Inter", sans-serif;
  letter-spacing: -0.01em;
`;

const ProjectDescription = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
  line-height: 1.6;
  flex-grow: 1;
  font-family: "Inter", sans-serif;
`;

const ProjectLink = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.75rem;
  background: transparent;
  color: white;
  text-decoration: none;
  font-weight: 600;
  border-radius: 12px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  font-family: "Inter", sans-serif;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.8), rgba(144, 19, 254, 0.8));
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
  }

  &:hover {
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);

    &::before {
      opacity: 1;
    }
  }
`;

const projectData = [
  {
    id: 1,
    image: "./resilientweb.png",
    title: "Resilient Risk Consulting",
    description: "Wix Studio Custom, Wix, VELO, HTML, JavaScript",
    category: "web",
    link: "https://www.resilientriskconsulting.com",
    color: "#4A90E2"
  },
  {
    id: 2,
    image: "./kaiteriteri.png",
    title: "Kaiteriteri NZ",
    description: "Silverstripe CMS, HTML, JavaScript, Python",
    category: "web",
    link: "https://www.experiencekaiteriteri.co.nz",
    color: "#27ae60"
  },
  {
    id: 3,
    image: "./kmtb.png",
    title: "Kaiteriteri MTB Park",
    description: "Squarespace Custom, HTML",
    category: "web",
    link: "https://bikekaiteriteri.co.nz/",
    color: "#2980b9"
  },
  {
    id: 4,
    image: "./Chris Hoy.png",
    title: "Chrishoy.com",
    description: "Wordpress, HTML, JavaScript, PHP",
    category: "web",
    link: "https://www.chrishoy.com",
    color: "#c0392b"
  },
  {
    id: 5,
    image: "./swingsuite.png",
    title: "The Swing Suite",
    description: "Wix Custom, Wix Velo, HTML, JavaScript. Custom data-rich booking platform integrating real-time dashboards.",
    category: "web",
    link: "https://www.theswingsuite.co.uk",
    color: "#34495e"
  },
  {
    id: 6,
    image: "./jerome.png",
    title: "Jerome Belau Ceramiste",
    description: "Squarespace Custom, HTML, JavaScript",
    category: "web",
    link: "https://www.jeromebelau.com",
    color: "#8e44ad"
  },
  {
    id: 7,
    image: "./BA.png",
    title: "Rebecca Adlington",
    description: "Wix Velo Custom, JavaScript",
    category: "web",
    link: "https://www.rebeccaadlington.co.uk",
    color: "#f39c12"
  },
  {
    id: 8,
    image: "./tgi.png",
    title: "TGI Sports",
    description: "Wordpress, HTML, PHP, JavaScript",
    category: "web",
    link: "https://www.tgisport.com",
    color: "#e74c3c"
  },
  {
    id: 9,
    image: "./swikeweb.png",
    title: "Swike",
    description: "JavaScript, Python, AWS",
    category: "app",
    link: "https://www.swike.com",
    color: "#9b59b6"
  },
  {
    id: 10,
    image: "./gt.png",
    title: "Guitar Tuner",
    description: "HTML, JavaScript, Python",
    category: "app",
    link: "https://tuneme-9e374.web.app/",
    color: "#3498db"
  },
  {
    id: 11,
    image: "./golfiq.png",
    title: "Golf IQ",
    description: "JavaScript, Firebase, Firestore, Vertex AI integration for smart golf analytics.",
    category: "app",
    link: "https://www.golfiq.me",
    color: "#16a085"
  },
  {
    id: 12,
    image: "./golfr_coach.png",
    title: "Golfr Coach",
    description: "A comprehensive coaching platform tailored for golf professionals. Enhances student-coach interaction, video analysis, and scheduling.",
    category: "web",
    link: "https://www.golfr.coach",
    color: "#27ae60"
  },
  {
    id: 13,
    image: "./highchair.png", // Newly captured screenshot
    title: "Highchair Social",
    description: "A vibrant social platform focusing on family dining experiences, connecting parents with child-friendly venues.",
    category: "web",
    link: "https://www.thehighchairsocial.co.uk",
    color: "#e67e22"
  },
  {
    id: 14,
    image: "./kingsmereland.png",
    title: "Kingsmere Land",
    description: "Corporate website for land development and real estate, featuring dynamic property showcases and investment information.",
    category: "web",
    link: "https://www.kingsmereland.co.uk",
    color: "#2980b9"
  },
  {
    id: 15,
    image: "./garland.png",
    title: "Garland PT",
    description: "Personal training and fitness platform offering personalized workout plans, client tracking, and nutritional guidance.",
    category: "web",
    link: "https://www.garlandpersonaltraining.co.uk/",
    color: "#c0392b"
  },
  {
    id: 16,
    image: "./elvinas.png",
    title: "Elvinas Osteopathic Clinic",
    description: "Professional healthcare clinic website with integrated booking systems, patient resources, and practitioner profiles.",
    category: "web",
    link: "https://www.elvinas-osteopathicclinic.co.uk",
    color: "#8e44ad"
  },
  {
    id: 17,
    image: "./ivaron.png",
    title: "Ivaron Group",
    description: "Corporate website displaying professional services with a modern, structured aesthetic.",
    category: "web",
    link: "https://www.ivarongroup.com/",
    color: "#E2A44A"
  },
  {
    id: 18,
    image: "./tourde4.png",
    title: "Tour de 4",
    description: "Fundraising cycling event website for Sir Chris Hoy's Tour de 4, featuring event countdown, donation integration and registration.",
    category: "web",
    link: "https://www.tourde4.com",
    color: "#c9a227"
  }
];

const Projects = () => {
  const [filter, setFilter] = useState("all");

  const filteredProjects = projectData.filter(
    (project) => filter === "all" || project.category === filter
  );

  return (
    <Container id="projects">
      <Title>Featured Projects</Title>
      <FilterContainer>
        <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>All</FilterButton>
        <FilterButton active={filter === "web"} onClick={() => setFilter("web")}>Websites</FilterButton>
        <FilterButton active={filter === "app"} onClick={() => setFilter("app")}>Applications</FilterButton>
      </FilterContainer>
      <ProjectGrid>
        {filteredProjects.map((project, index) => (
          <ProjectCard
            key={project.id}
            index={index}
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: Math.min(index * 0.1, 0.5), ease: "easeOut" }}
          >
            <ImageContainer $fallbackBg={`linear-gradient(135deg, #1c1c28, ${project.color}40)`}>
              {project.image ? (
                <ProjectImage src={project.image} alt={project.title} />
              ) : (
                <FallbackText>{project.title.substring(0, 2).toUpperCase()}</FallbackText>
              )}
            </ImageContainer>
            <ProjectInfo>
              <CategoryBadge>{project.category}</CategoryBadge>
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              <ProjectLink>
                Visit Website <span style={{ marginLeft: "0.5rem" }}>→</span>
              </ProjectLink>
            </ProjectInfo>
          </ProjectCard>
        ))}
      </ProjectGrid>
    </Container>
  );
};

export default Projects;
